import React, { useCallback, useMemo, useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Tooltip } from "antd"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDevice } from "hooks/useDevice"
import { usePermissions } from "hooks/usePermissions"
import useActiveGuide from "hooks/useActiveClient"
import { routes } from "router"
import { DATE_FORMAT } from "modules/datepicker/constants"
import { MODULES, ACTIONS } from "utils/permissions"
import moment from "moment"
import { useStripeIssue } from "hooks/useStripeIssue"
import { DEFAULT_RANGE, CALENDAR_TABS, DEFAULT_START_HOUR, DEFAULT_END_HOUR } from "../constants"
import styles from "../Calendar.module.css"
import useHeaderFilters from "./hooks/useHeaderFilters"
import BookingTypeTabs from "./BookingTypeTabs"
import DateNavRow from "./DateNavRow"
import DesktopFiltersRow from "./DesktopFiltersRow"
import DesktopFilterPills from "./DesktopFilterPills"
import LegendPopover from "./LegendPopover"
import CalendarSearch from "./CalendarSearch"
import AddBlackoutButton from "./AddBlackoutButton"
import TimeRangeSettings from "./TimeRangeSettings"
import MobileFiltersDrawer from "./MobileFiltersDrawer"
import FilterChips from "./FilterChips"

const Header = ({
  params,
  onChangeFilters,
  handleJournalClick,
  setIsScheduleTimeOffModalOpen,
  onFiltersChange,
  startHour,
  endHour,
  onStartHourChange,
  onEndHourChange,
  onResetHours,
  hasMyBookings,
  savedFilters,
  onBlackoutCreated
}) => {
  const activeTab = params.tab || CALENDAR_TABS.ALL_BOOKINGS
  const isAgendaView = params.range === "week" && (params.agenda === true || params.agenda === "true")
  const isListView = params.range === "week" && params.listview
  const agendaChecked = params.agenda === true || params.agenda === "true"

  const showTimeRangeSettings = useMemo(() => {
    const isDayView = params.range === "date"
    const isWeekView = params.range === "week"

    if (activeTab === CALENDAR_TABS.ALL_BOOKINGS) {
      return isDayView || isAgendaView
    }
    if (activeTab === CALENDAR_TABS.MY_BOOKINGS) {
      return isDayView || isWeekView
    }
    if (activeTab === CALENDAR_TABS.CREW || activeTab === CALENDAR_TABS.EQUIPMENT) {
      return isDayView
    }
    return false
  }, [params.range, activeTab, isAgendaView])

  const { t } = useTranslation()
  const { isMobile, isTablet } = useDevice()
  const { hasPermission, hasFullAccessFlag } = usePermissions()
  const { isActive: active } = useActiveGuide()
  const navigate = useNavigate()

  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)
  const isDesktop = !(isMobile || isTablet)
  const { hasStripeIssue, stripeIssueMessage } = useStripeIssue()
  const usesFilterPills = activeTab !== CALENDAR_TABS.CREW && activeTab !== CALENDAR_TABS.EQUIPMENT

  const date = useMemo(() => {
    if (params.date && moment(params.date, DATE_FORMAT, true).isValid()) return params.date
    return moment().format(DATE_FORMAT)
  }, [params.date])
  const dateMoment = useMemo(() => moment(date, DATE_FORMAT, true), [date])
  const dateSwitcherType = isListView ? "date" : params.range
  const dateSwitcherKey = dateSwitcherType || "date"

  const getPopupContainer = useCallback((node) => node?.parentElement || document.body, [])
  const calendarFilterSelectProps = useMemo(() => ({ getPopupContainer }), [getPopupContainer])

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [viewPopoverOpen, setViewPopoverOpen] = useState(false)

  const {
    filters,
    crewFilters,
    setFilters,
    setCrewFilters,
    handleFilterChange,
    experienceOptions,
    crewMemberOptionsWithUnassigned,
    equipmentOptions,
    equipmentOptionsWithUnassigned,
    setEquipmentCategoryOptions,
    filterChips,
    roles,
    crewLoaded,
    equipmentLoaded
  } = useHeaderFilters({ savedFilters, activeBookingType: activeTab, onFiltersChange })

  useEffect(() => {
    if (!params.range) onChangeFilters({ target: { value: DEFAULT_RANGE, name: "range" } })
  }, [params.range]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleNewBooking = useCallback(() => navigate(routes.guideBookingNewPath()), [navigate])
  const handleGoToToday = useCallback(
    () => onChangeFilters({ target: { name: "date", value: moment().format(DATE_FORMAT) } }),
    [onChangeFilters]
  )
  const handleRangeChange = useCallback((key) => onChangeFilters({ target: { name: "range", value: key } }), [onChangeFilters])
  const handleAgendaChange = useCallback(
    (checked) => {
      onChangeFilters({ target: { name: "agenda", value: checked } })
      onFiltersChange({ agenda_view: checked })
    },
    [onChangeFilters, onFiltersChange]
  )

  const filterBadgeCount =
    activeTab === CALENDAR_TABS.EQUIPMENT
      ? (filters.equipmentFilters?.equipment?.length || 0) + (filters.equipmentFilters?.category?.length || 0)
      : filters.experience.length + filters.crew.length + filters.equipment.length

  const sharedFilterProps = {
    activeTab,
    params,
    filters,
    crewFilters,
    setFilters,
    setCrewFilters,
    handleFilterChange,
    experienceOptions,
    crewMemberOptionsWithUnassigned,
    equipmentOptions,
    equipmentOptionsWithUnassigned,
    roles,
    calendarFilterSelectProps,
    setEquipmentCategoryOptions,
    setIsScheduleTimeOffModalOpen,
    handleNewBooking,
    active,
    crewLoaded,
    equipmentLoaded,
    onBlackoutCreated
  }

  const timeRangeSettings = showTimeRangeSettings ? (
    <TimeRangeSettings
      startHour={startHour}
      endHour={endHour}
      onStartChange={onStartHourChange}
      onEndChange={onEndHourChange}
      onReset={onResetHours}
      isModified={startHour !== DEFAULT_START_HOUR || endHour !== DEFAULT_END_HOUR}
    />
  ) : null

  /* The design puts these on the tabs row, so the row below is the date and filter toolbar. */
  const headerActions = isDesktop ? (
    <Flex align="center" gap={10} className={styles.calendarActions}>
      {/* The guide/admin handles blackouts; crew and staff schedule their own time off */}
      {hasFullAccessFlag ? (
        <AddBlackoutButton isDisabled={!active} onCreated={onBlackoutCreated} />
      ) : (
        <Button
          color="primary"
          variant="outlined"
          shape="round"
          className={styles.scheduleTimeOff}
          size="large"
          onClick={active ? () => setIsScheduleTimeOffModalOpen(true) : undefined}
          disabled={!active}
        >
          {t("unavailable_days.schedule_time_off")}
        </Button>
      )}

      {canCreateBooking && (
        <Tooltip title={hasStripeIssue ? stripeIssueMessage : ""}>
          <span className={hasStripeIssue ? styles.disabledBtnWrapper : undefined}>
            <Button
              type="primary"
              shape="round"
              className={styles.newBooking}
              size="large"
              onClick={active && !hasStripeIssue ? handleNewBooking : undefined}
              disabled={!active || hasStripeIssue}
              style={hasStripeIssue ? { pointerEvents: "none" } : undefined}
            >
              {t("bookings.add")}
            </Button>
          </span>
        </Tooltip>
      )}
      {timeRangeSettings}
    </Flex>
  ) : null

  if (!params.range) return null

  return (
    <Flex vertical className={styles.calendarHeader}>
      <BookingTypeTabs
        activeTab={activeTab}
        hasMyBookings={hasMyBookings}
        onChangeFilters={onChangeFilters}
        actions={headerActions}
      />

      <Flex
        align="center"
        justify="space-between"
        gap={16}
        wrap
        className={isDesktop ? `px-3xl py-s ${styles.calendarToolbar}` : undefined}
      >
        <DateNavRow
        params={params}
        activeTab={activeTab}
        isDesktop={isDesktop}
        isTablet={isTablet}
        isMobile={isMobile}
        date={date}
        dateMoment={dateMoment}
        dateSwitcherKey={dateSwitcherKey}
        dateSwitcherType={dateSwitcherType}
        filterBadgeCount={filterBadgeCount}
        agendaChecked={agendaChecked}
        viewPopoverOpen={viewPopoverOpen}
        setViewPopoverOpen={setViewPopoverOpen}
        setMobileFiltersOpen={setMobileFiltersOpen}
        onChangeFilters={onChangeFilters}
        onRangeChange={handleRangeChange}
        onAgendaChange={handleAgendaChange}
        onGoToToday={handleGoToToday}
        />

        {isDesktop && (
          <Flex align="center" gap={16} className={styles.calendarToolbarEnd} wrap>
            <LegendPopover />
            {usesFilterPills ? (
              <DesktopFilterPills
                filters={filters}
                setFilters={setFilters}
                handleFilterChange={handleFilterChange}
                experienceOptions={experienceOptions}
                crewMemberOptionsWithUnassigned={crewMemberOptionsWithUnassigned}
                equipmentOptionsWithUnassigned={equipmentOptionsWithUnassigned || equipmentOptions}
              />
            ) : (
              <DesktopFiltersRow {...sharedFilterProps} />
            )}
            <CalendarSearch value={savedFilters?.search || ""} onChange={(search) => onFiltersChange({ search })} />
          </Flex>
        )}
      </Flex>

      {(isMobile || isTablet) && <MobileFiltersDrawer open={mobileFiltersOpen} setOpen={setMobileFiltersOpen} {...sharedFilterProps} />}

      <FilterChips chips={filterChips} />
    </Flex>
  )
}

Header.propTypes = {
  params: PropTypes.shape({
    date: PropTypes.string,
    range: PropTypes.string,
    tab: PropTypes.string,
    agenda: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    listview: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  handleJournalClick: PropTypes.func,
  setIsScheduleTimeOffModalOpen: PropTypes.func.isRequired,
  onFiltersChange: PropTypes.func,
  startHour: PropTypes.number,
  endHour: PropTypes.number,
  onStartHourChange: PropTypes.func,
  onEndHourChange: PropTypes.func,
  onResetHours: PropTypes.func,
  hasMyBookings: PropTypes.bool,
  savedFilters: PropTypes.object,
  onBlackoutCreated: PropTypes.func
}

export default Header
